import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    Box, Typography, Card, CardContent, Tabs, Tab, Button,
    TextField, IconButton, Checkbox, Divider, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Grid,
    Select, FormControl, Tooltip, Autocomplete, Chip,
} from "@mui/material";
import {
    ArrowBack, CheckCircle, Cancel, CallOutlined, ChatOutlined,
    Handshake, AddTask, Close, EditOutlined, CheckOutlined,
    PhoneOutlined, EmailOutlined, BusinessOutlined, PersonOutlined, ContentCopyOutlined,
    VisibilityOutlined, MessageOutlined, LocalOfferOutlined, EventRepeat, LocationOnOutlined,
    ContactPageOutlined,
} from "@mui/icons-material";
import { PieChart, LineChart } from "@mui/x-charts";
import Cookies from "js-cookie";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";
import CRMAvatar from "../common/CRMAvatar";
import CRMBadge from "../common/CRMBadge";
import CRMProductServiceDetailDialog from "../common/CRMProductServiceDetailDialog";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import { sortStagesByOrdering } from "./pipelineStageUtils";
import { normalizeDealDetailResponse, resolveContactTags } from "../common/crmContactTags";
import {
    TASK_DATE_PRESETS,
    buildTaskDateQueryParams,
    expandTaskDateRangeToInclude,
    getTaskDateRangeStatusMessage,
    getTaskDateFilterEmptyMessage,
    getTaskDueDateRangeForMonths,
    isTaskDateFilterActive,
    isTaskDateFilterComplete,
    isValidTaskDateRange,
    matchTaskDatePresetMonths,
} from "../common/crmTaskDateRange";
import { useNavigate, useParams } from "react-router-dom";

const QUICK_ACTIONS = [
    { icon: <ChatOutlined fontSize="small" />, label: "Send WhatsApp" },
    { icon: <CallOutlined fontSize="small" />, label: "Log Call" },
    { icon: <MessageOutlined fontSize="small" />, label: "Log Message" },
    { icon: <EventRepeat fontSize="small" />, label: "Create Follow-up" },
    { icon: <AddTask fontSize="small" />, label: "Add Task" },
];

const TAG_COLOR_PALETTE = [
    { bg: "#fef3c7", text: "#92400e" },
    { bg: "#e0e7ff", text: "#3730a3" },
    { bg: "#d1fae5", text: "#065f46" },
    { bg: "#fce7f3", text: "#9d174d" },
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#ffe4e6", text: "#be123c" },
];

const taskIcon = (type) =>
    type === "call" ? "📞"
        : type === "message" ? "💬"
            : type === "follow-up" ? "🔁"
                : "🤝";

const DEAL_STATUS_LABELS = {
    open:  "Open",
    won:   "Won",
    lost:  "Lost",
};

const TASK_STATUS_LABELS = {
    pending:   "Pending",
    completed: "Completed",
    overdue:   "Overdue",
};

const TASK_TYPE_LABELS = {
    call:         "Call",
    message:      "Message",
    meeting:      "Meeting",
    "follow-up":  "Follow-up",
};

const TASK_TYPES = ["call", "message", "meeting", "follow-up"];

const DUE_DATE_PRESETS = [
    { label: "1d", days: 1 },
    { label: "3d", days: 3 },
    { label: "5d", days: 5 },
    { label: "1w", days: 7 },
    { label: "2w", days: 14 },
];

const addDaysYmd = (days) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const getCurrentUserId = () => {
    const raw = Cookies.get("userId");
    if (raw == null || raw === "") return "";
    try {
        const parsed = JSON.parse(raw);
        return parsed != null ? String(parsed) : "";
    } catch {
        return String(raw);
    }
};

const activityIcon = (type, source) => {
    if (source === "note") return "📝";
    switch (type) {
        case "note":          return "📝";
        case "message":       return "💬";
        case "call":          return "📞";
        case "stage":
        case "status_change": return "🔀";
        case "task":          return "✅";
        default:              return "📌";
    }
};

const fmtDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const fmtValue = (val) => {
    if (!val && val !== 0) return "—";
    const n = parseFloat(val);
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000)   return `₹${(n / 1000).toFixed(1)}k`;
    return `₹${n}`;
};

const fmtPercent = (val) => {
    const n = Number(val);
    if (Number.isNaN(n)) return "0%";
    return `${n.toFixed(1)}%`;
};

const startCase = (str) =>
    String(str || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (m) => m.toUpperCase());

const normalizeDistributionPairs = (raw, preferredKey = "type") => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw
            .map((item, i) => {
                if (typeof item === "string" || typeof item === "number") {
                    return { key: String(i), value: Number(item) || 0 };
                }
                if (!item || typeof item !== "object") return null;
                const key =
                    item.key ??
                    item.label ??
                    item.name ??
                    item.type ??
                    item.status ??
                    item[preferredKey] ??
                    String(i);
                const value =
                    item.value ??
                    item.count ??
                    item.total ??
                    item.items ??
                    0;
                return { key: String(key), value: Number(value) || 0 };
            })
            .filter(Boolean);
    }
    if (typeof raw === "object") {
        return Object.entries(raw).map(([k, v], i) => {
            if (v && typeof v === "object") {
                const key = v.key ?? v.label ?? v.name ?? v.type ?? v.status ?? k ?? String(i);
                const value = v.value ?? v.count ?? v.total ?? 0;
                return { key: String(key), value: Number(value) || 0 };
            }
            return { key: String(k), value: Number(v) || 0 };
        });
    }
    return [];
};

/** Normalizes linked products/services from deal load (supports legacy field names). */
const normalizeDealProducts = (dealData) => {
    if (!dealData) return [];
    const mapRow = (x) => {
        if (!x || typeof x !== "object") return null;
        const id = Number(
            x.id ??
                x.pivot?.product_service_id ??
                x.product_service_id ??
                x.crm_product_service_id
        );
        if (Number.isNaN(id)) return null;
        return {
            ...x,
            id,
            name: String(x.name ?? "").trim() || "Unnamed",
            type: x.type ?? "product",
        };
    };
    if (Array.isArray(dealData.product_services)) {
        return dealData.product_services.map(mapRow).filter(Boolean);
    }
    const legacyRaw =
        dealData.products ??
        dealData.products_services ??
        dealData.crm_products_services ??
        dealData.attached_products;
    if (Array.isArray(legacyRaw) && legacyRaw.length) {
        return legacyRaw.map(mapRow).filter(Boolean);
    }
    const ids = dealData.product_service_ids ?? dealData.product_ids;
    if (Array.isArray(ids) && ids.length) {
        return ids
            .map((id) => ({ id: Number(id), name: `Catalog #${id}`, type: "product", price: null }))
            .filter((x) => !Number.isNaN(x.id));
    }
    return [];
};

const catalogOptionLabel = (p) => {
    if (!p) return "";
    const kind = p.type === "service" ? "Service" : "Product";
    const price = p.price != null && p.price !== "" ? fmtValue(p.price) : "—";
    return `${p.name} · ${kind} · ${price}`;
};

const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
};

// ─── Add Task / Follow-up Dialog ──────────────────────────────────────────────
const EMPTY_TASK = { title: "", type: "call", description: "", due_date: "", assign_user_id: "" };
const TYPE_ICONS = { call: "📞", message: "💬", meeting: "🤝", "follow-up": "🔁" };

const AddTaskDialog = ({ open, dealId, onClose, onCreated, mode = "task" }) => {
    const isFollowUp = mode === "follow-up";
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [form, setForm]         = useState(EMPTY_TASK);
    const [saving, setSaving]     = useState(false);
    const [errors, setErrors]     = useState({});
    const [users, setUsers]       = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [duePreset, setDuePreset] = useState(null);

    // Fetch allowed users when dialog opens
    useEffect(() => {
        if (!open) return;
        const defaultDueDate = isFollowUp ? addDaysYmd(1) : "";
        setForm({
            title: "",
            type: isFollowUp ? "follow-up" : "call",
            description: "",
            due_date: defaultDueDate,
            assign_user_id: isFollowUp ? getCurrentUserId() : "",
        });
        setDuePreset(isFollowUp ? "1d" : null);
        setErrors({});

        const fetchUsers = async () => {
            setLoadingUsers(true);
            try {
                const res  = await axiosInstance.get(`${base_url}/whatsapp-business/users/allowed`);
                const list = Array.isArray(res.data?.data) ? res.data.data : [];
                setUsers(list);
            } catch (_) {
                setUsers([]);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [open, isFollowUp]);

    const set = (field) => (e) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [field]: value }));
        if (field === "due_date") {
            const match = DUE_DATE_PRESETS.find((p) => addDaysYmd(p.days) === value);
            setDuePreset(match ? match.label : null);
        }
        if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
    };

    const applyDuePreset = (preset) => {
        const due_date = addDaysYmd(preset.days);
        setDuePreset(preset.label);
        setForm((f) => ({ ...f, due_date }));
        if (errors.due_date) setErrors((p) => ({ ...p, due_date: "" }));
    };

    const handleSubmit = async () => {
        if (!form.title.trim()) { setErrors({ title: "Title is required" }); return; }
        const dueDate = form.due_date || (isFollowUp ? addDaysYmd(1) : "");
        setSaving(true);
        try {
            const payload = {
                deal_id: dealId,
                title:   form.title.trim(),
                type:    form.type,
                status:  "pending",
            };
            if (form.description) payload.description = form.description.trim();
            if (dueDate)          payload.due_date    = dueDate;
            if (form.assign_user_id) {
                payload.assign_user = Number(form.assign_user_id);
            }

            await axiosInstance.post(`${base_url}/crm/tasks`, payload);
            notifyRef.current(
                isFollowUp ? "Follow-up created successfully" : "Task added successfully",
                "success"
            );
            onCreated({ dueDate, isFollowUp });
            onClose();
        } catch (err) {
            notifyRef.current(
                err?.response?.data?.message || (isFollowUp ? "Failed to create follow-up" : "Failed to create task"),
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    const dialogTitle = isFollowUp ? "Create Follow-up" : "Add Task";
    const submitLabel = saving
        ? (isFollowUp ? "Creating…" : "Adding…")
        : (isFollowUp ? "Create Follow-up" : "Add Task");

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(17,24,39,0.06)", display: "grid", placeItems: "center" }}>
                        {isFollowUp
                            ? <EventRepeat sx={{ fontSize: 16, color: "text.primary" }} />
                            : <AddTask sx={{ fontSize: 16, color: "text.primary" }} />}
                    </Box>
                    {dialogTitle}
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving}><Close fontSize="small" /></IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2}>
                    {/* Title */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth required
                            label={isFollowUp ? "Follow-up Title" : "Task Title"}
                            placeholder={isFollowUp ? "e.g. Check in after proposal review" : "e.g. Follow up call with client"}
                            value={form.title}
                            onChange={set("title")}
                            size="small"
                            error={!!errors.title}
                            helperText={errors.title || ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {/* Type */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select fullWidth
                            label="Task Type"
                            value={form.type}
                            onChange={set("type")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            {TASK_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {TYPE_ICONS[t]}&nbsp;{TASK_TYPE_LABELS[t] || t}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Due Date */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Due Date"
                            type="date"
                            value={form.due_date}
                            onChange={set("due_date")}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>

                    {isFollowUp && (
                        <Grid item xs={12}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.75 }}>
                                Due in
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                {DUE_DATE_PRESETS.map((preset) => {
                                    const selected = duePreset === preset.label;
                                    return (
                                        <Chip
                                            key={preset.label}
                                            label={preset.label}
                                            size="small"
                                            clickable
                                            onClick={() => applyDuePreset(preset)}
                                            variant={selected ? "filled" : "outlined"}
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                                borderRadius: 1.5,
                                                ...(selected
                                                    ? {
                                                        backgroundColor: "#111827",
                                                        color: "#fff",
                                                        "&:hover": { backgroundColor: "#1f2937" },
                                                    }
                                                    : {
                                                        borderColor: WB_UI.color.border,
                                                        "&:hover": { backgroundColor: "rgba(17,24,39,0.04)" },
                                                    }),
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Grid>
                    )}

                    {/* Assign To — dropdown from API */}
                    <Grid item xs={12}>
                        <TextField
                            select fullWidth
                            label="Assign To"
                            value={form.assign_user_id}
                            onChange={set("assign_user_id")}
                            size="small"
                            disabled={loadingUsers}
                            helperText={loadingUsers ? "Loading team members…" : ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            <MenuItem value=""><em>Unassigned</em></MenuItem>
                            {users.map((u) => (
                                <MenuItem key={u.id} value={String(u.id)}>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{u.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth multiline rows={3}
                            label="Description"
                            placeholder={isFollowUp ? "Optional notes about this follow-up..." : "Optional notes about this task..."}
                            value={form.description}
                            onChange={set("description")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} size="small" disabled={saving} sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button
                    variant="contained" size="small"
                    onClick={handleSubmit}
                    disabled={saving || !form.title.trim()}
                    sx={WB_SX.primaryButton}
                >
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const getTaskAssignUserId = (t) => {
    if (!t) return "";
    if (t.assign_user_id != null) return String(t.assign_user_id);
    if (t.assigned_user_id != null) return String(t.assigned_user_id);
    if (t.user_id != null) return String(t.user_id);
    if (t.assigned_user?.id != null) return String(t.assigned_user.id);
    if (typeof t.assign_user === "number") return String(t.assign_user);
    return "";
};

const getTaskAssigneeName = (t) => {
    if (!t) return "";
    if (t.assigned_user?.name) return t.assigned_user.name;
    if (t.assign_user_name) return t.assign_user_name;
    if (t.current_assigned_user?.name) return t.current_assigned_user.name;
    if (t.assign_user && typeof t.assign_user === "object" && t.assign_user.name) return t.assign_user.name;
    if (t.assign_user != null) return String(t.assign_user);
    return "";
};

const getTaskDisplayTitle = (t) => {
    if (!t) return "Untitled task";
    return t.title || t.task_title || t.task_name || t.name || "Untitled task";
};

// ─── Edit Task Dialog — PUT /api/crm/tasks/{id} ───────────────────────────────
const EditTaskDialog = ({ open, task, onClose, onUpdated }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [form, setForm]         = useState({ title: "", type: "call", description: "", due_date: "", assign_user_id: "", status: "pending" });
    const [saving, setSaving]     = useState(false);
    const [errors, setErrors]     = useState({});
    const [users, setUsers]       = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        const fetchUsers = async () => {
            setLoadingUsers(true);
            try {
                const res  = await axiosInstance.get(`${base_url}/whatsapp-business/users/allowed`);
                const list = Array.isArray(res.data?.data) ? res.data.data : [];
                setUsers(list);
            } catch (_) {
                setUsers([]);
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [open]);

    useEffect(() => {
        if (!open || !task) return;
        setForm({
            title:         task.title || "",
            type:          task.type || "call",
            description:   task.description || "",
            due_date:      task.due_date ? String(task.due_date).split("T")[0] : "",
            assign_user_id: getTaskAssignUserId(task),
            status:        task.status || "pending",
        });
    }, [open, task]);

    const set = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
    };

    const handleSubmit = async () => {
        if (!task?.id) return;
        if (!form.title.trim()) { setErrors({ title: "Title is required" }); return; }
        setSaving(true);
        try {
            const payload = {
                title:  form.title.trim(),
                type:   form.type,
                status: form.status,
            };
            payload.description = form.description.trim() ? form.description.trim() : "";
            if (form.due_date) payload.due_date = form.due_date;
            if (form.assign_user_id) payload.assign_user = Number(form.assign_user_id);

            await axiosInstance.put(`${base_url}/crm/tasks/${task.id}`, payload);
            notifyRef.current("Task updated", "success");
            onUpdated();
            onClose();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to update task", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(59,130,246,0.08)", display: "grid", placeItems: "center" }}>
                        <EditOutlined sx={{ fontSize: 16, color: "primary.main" }} />
                    </Box>
                    Edit task
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving}><Close fontSize="small" /></IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth required
                            label="Task title"
                            value={form.title}
                            onChange={set("title")}
                            size="small"
                            error={!!errors.title}
                            helperText={errors.title || ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select fullWidth
                            label="Type"
                            value={form.type}
                            onChange={set("type")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            {TASK_TYPES.map((tp) => (
                                <MenuItem key={tp} value={tp}>
                                    {TYPE_ICONS[tp]}&nbsp;{TASK_TYPE_LABELS[tp] || tp}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select fullWidth
                            label="Status"
                            value={form.status}
                            onChange={set("status")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="overdue">Overdue</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Due date"
                            type="date"
                            value={form.due_date}
                            onChange={set("due_date")}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            select fullWidth
                            label="Assign to"
                            value={form.assign_user_id}
                            onChange={set("assign_user_id")}
                            size="small"
                            disabled={loadingUsers}
                            helperText={loadingUsers ? "Loading team members…" : ""}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        >
                            <MenuItem value=""><em>Unassigned</em></MenuItem>
                            {users.map((u) => (
                                <MenuItem key={u.id} value={String(u.id)}>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{u.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth multiline rows={3}
                            label="Description"
                            value={form.description}
                            onChange={set("description")}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} size="small" disabled={saving} sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button variant="contained" size="small" onClick={handleSubmit} disabled={saving || !form.title.trim()} sx={WB_SX.primaryButton}>
                    {saving ? "Saving…" : "Save changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Edit Note Dialog — PUT /api/crm/notes/{id} (body: { note })
const EditNoteDialog = ({ open, note, onClose, onUpdated }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && note) setText(note.note || note.text || "");
    }, [open, note]);

    const handleSubmit = async () => {
        if (!note?.id || !text.trim()) return;
        setSaving(true);
        try {
            await axiosInstance.put(`${base_url}/crm/notes/${note.id}`, { note: text.trim() });
            notifyRef.current("Note updated", "success");
            onUpdated();
            onClose();
        } catch (err) {
            const st = err?.response?.status;
            const msg = err?.response?.data?.message
                || (st === 403
                    ? "You don't have permission to edit this note (CRM access rules)."
                    : st === 404
                        ? "Note not found."
                        : "Failed to update note");
            notifyRef.current(msg, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EditOutlined sx={{ fontSize: 18, color: "primary.main" }} />
                    Edit note
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving}><Close fontSize="small" /></IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                <TextField
                    fullWidth multiline rows={4}
                    label="Note"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    size="small"
                    disabled={saving}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} size="small" disabled={saving} sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button variant="contained" size="small" onClick={handleSubmit} disabled={saving || !text.trim()} sx={WB_SX.primaryButton}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Edit deal — PUT /api/crm/deals/{id} (title, description, value, expected_close_date)
const EditDealDetailsDialog = ({ open, dealId, initialTitle, initialDescription, onClose, onSaved }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setTitle(initialTitle || "");
            setDescription(initialDescription || "");
        }
    }, [open, initialTitle, initialDescription]);

    const handleSubmit = async () => {
        if (!dealId) return;
        setSaving(true);
        try {
            const payload = { description: description.trim() };
            if (title.trim()) payload.title = title.trim();
            await axiosInstance.put(`${base_url}/crm/deals/${dealId}`, payload);
            notifyRef.current("Deal updated", "success");
            onSaved();
            onClose();
        } catch (err) {
            const st = err?.response?.status;
            const msg = err?.response?.data?.message
                || (st === 403
                    ? "You don't have permission to change this deal (CRM access rules)."
                    : "Failed to update deal");
            notifyRef.current(msg, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: "1rem", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EditOutlined sx={{ fontSize: 18, color: "primary.main" }} />
                    Edit deal
                </Box>
                <IconButton size="small" onClick={onClose} disabled={saving}><Close fontSize="small" /></IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Deal title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} size="small" disabled={saving} sx={WB_SX.secondaryButton}>Cancel</Button>
                <Button variant="contained" size="small" onClick={handleSubmit} disabled={saving || !title.trim()} sx={WB_SX.primaryButton}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Deal Detail ──────────────────────────────────────────────────────────────
const CRMDealDetail = ({ deal: dealProp, onBack }) => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;
    const navigate = useNavigate();
    const { id: routeDealId } = useParams();

    const [deal, setDeal]           = useState(null);
    const [tasks, setTasks]         = useState([]);
    const [totalFollowUps, setTotalFollowUps] = useState(0);
    const [dateFrom, setDateFrom]   = useState("");
    const [dateTo, setDateTo]       = useState("");
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [notes, setNotes]         = useState([]);
    const [timeline, setTimeline]   = useState([]);
    const [effort, setEffort]       = useState(null);
    const [loadingEffort, setLoadingEffort] = useState(false);
    const [effortError, setEffortError] = useState("");
    const [tab, setTab]             = useState(0);
    const [noteInput, setNoteInput]   = useState("");
    const [loading, setLoading]       = useState(true);
    const [savingNote, setSavingNote] = useState(false);
    const [actioning, setActioning]     = useState(false);
    const [addTaskOpen, setAddTaskOpen] = useState(false);
    const [addFollowUpOpen, setAddFollowUpOpen] = useState(false);
    const [editTask, setEditTask]       = useState(null);
    const [editNote, setEditNote]       = useState(null);
    const [dealEditOpen, setDealEditOpen] = useState(false);
    const [wonConfirmOpen, setWonConfirmOpen]   = useState(false);
    const [lostConfirmOpen, setLostConfirmOpen] = useState(false);
    const [loggingCall, setLoggingCall]         = useState(false);
    const [loggingMessage, setLoggingMessage]   = useState(false);

    /** Linked products/services on the deal; kept in sync when the deal is saved */
    const [dealLineItems, setDealLineItems]     = useState([]);
    const [catalogProducts, setCatalogProducts] = useState([]);
    const [loadingCatalog, setLoadingCatalog]   = useState(false);
    const [savingDealProducts, setSavingDealProducts] = useState(false);
    const [productDetailOpen, setProductDetailOpen]     = useState(false);
    const [productDetailId, setProductDetailId]         = useState(null);
    const [productDetailInitial, setProductDetailInitial] = useState(null);

    // Stage editing
    const [stages, setStages]           = useState([]);
    const [stageEditing, setStageEditing] = useState(false);
    const [selectedStageId, setSelectedStageId] = useState("");
    const [updatingStage, setUpdatingStage]     = useState(false);

    const dealId = routeDealId || dealProp?.id;
    const displayDeal  = deal || dealProp;
    const contactId = displayDeal?.contact_id || displayDeal?.contact?.id;
    const handleBack = onBack || (() => navigate("/crm/pipeline"));

    // All CRM endpoints return: { success, data: { data: [...], total, ... } }
    const extractList = (res) => {
        const paginator = res.data?.data;
        if (paginator && Array.isArray(paginator.data)) return paginator.data;
        if (Array.isArray(res.data?.data))               return res.data.data;
        if (Array.isArray(res.data))                     return res.data;
        return [];
    };

    const extractTotal = (res, fallbackLength = 0) => {
        const paginator = res?.data?.data;
        if (paginator && typeof paginator.total === "number") return paginator.total;
        if (typeof res?.data?.total === "number") return res.data.total;
        return fallbackLength;
    };

    const fetchDeal = useCallback(async () => {
        if (!dealId) return;
        setLoading(true);
        try {
            const dealRes = await axiosInstance.get(`${base_url}/crm/deals/${dealId}`);
            // Deal detail: { success, data: { id, title, contact: { tags, ... }, ... } }
            const raw = dealRes.data?.data ?? dealRes.data?.deal ?? dealRes.data;
            const dealData = normalizeDealDetailResponse(raw);
            setDeal(dealData);
            setDealLineItems(normalizeDealProducts(dealData));
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to load deal", "error");
        } finally {
            setLoading(false);
        }
    }, [dealId]);

    const fetchTasks = useCallback(async (from = dateFrom, to = dateTo) => {
        if (!dealId) return;
        if (!isTaskDateFilterComplete(from, to)) return;
        const dateParams = buildTaskDateQueryParams(from, to);
        if (dateParams === null) {
            notifyRef.current("To date must be on or after From date", "error");
            return;
        }
        setLoadingTasks(true);
        try {
            const tasksRes = await axiosInstance.get(`${base_url}/crm/tasks`, {
                params: {
                    deal_id: dealId,
                    per_page: 50,
                    ...dateParams,
                },
            });
            const taskList = extractList(tasksRes);
            setTasks(taskList);
            setTotalFollowUps(extractTotal(tasksRes, taskList.length));
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to load tasks", "error");
        } finally {
            setLoadingTasks(false);
        }
    }, [dealId, dateFrom, dateTo]);

    const fetchCatalogProducts = useCallback(async () => {
        if (!dealId) return;
        setLoadingCatalog(true);
        try {
            const res = await axiosInstance.get(`${base_url}/crm/products-services`, { params: { per_page: 200 } });
            const paginator = res.data?.data ?? {};
            const list = Array.isArray(paginator.data) ? paginator.data : [];
            setCatalogProducts(list);
        } catch (_) {
            setCatalogProducts([]);
        } finally {
            setLoadingCatalog(false);
        }
    }, [dealId]);

    useEffect(() => {
        fetchCatalogProducts();
    }, [fetchCatalogProducts]);

    /** Prefer full catalog row; keep deal pivot timestamps when present. */
    const lineItemsForAutocomplete = useMemo(
        () =>
            dealLineItems.map((p) => {
                const c = catalogProducts.find((x) => x.id === p.id);
                if (!c) return p;
                const pivot = p.pivot ?? c.pivot;
                return pivot ? { ...c, pivot } : { ...c };
            }),
        [dealLineItems, catalogProducts]
    );

    const openProductDetail = (item) => {
        if (!item?.id) return;
        setProductDetailId(item.id);
        setProductDetailInitial(item);
        setProductDetailOpen(true);
    };

    const saveDealProducts = async () => {
        if (!dealId) return;
        setSavingDealProducts(true);
        try {
            const product_service_ids = dealLineItems
                .map((p) => Number(p.id))
                .filter((id) => !Number.isNaN(id));
            await axiosInstance.put(`${base_url}/crm/deals/${dealId}`, { product_service_ids });
            notifyRef.current("Products and services saved on this deal.", "success");
            fetchDeal();
            fetchTimeline();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Could not save products and services on this deal.", "error");
        } finally {
            setSavingDealProducts(false);
        }
    };

    const fetchTimeline = useCallback(async () => {
        if (!contactId) return;
        try {
            const res = await axiosInstance.get(`${base_url}/crm/contacts/${contactId}/timeline`, { params: { per_page: 20 } });
            setTimeline(extractList(res));
        } catch (_) {}
    }, [contactId]);

    const fetchNotes = useCallback(async () => {
        if (!contactId) return;
        try {
            // GET .../notes?deal_id= — optional filter; omit = all notes for contact (API doc)
            const params = {};
            if (dealId) params.deal_id = dealId;
            const res = await axiosInstance.get(`${base_url}/crm/contacts/${contactId}/notes`, { params });
            setNotes(extractList(res));
        } catch (_) {}
    }, [contactId, dealId]);

    const fetchEffort = useCallback(async () => {
        if (!dealId) return;
        setLoadingEffort(true);
        setEffortError("");
        try {
            const res = await axiosInstance.get(`${base_url}/crm/${dealId}/effort`);
            const effortData = res?.data?.data ?? res?.data ?? null;
            setEffort(effortData);
        } catch (err) {
            setEffort(null);
            setEffortError(err?.response?.data?.message || "Failed to load effort analytics");
        } finally {
            setLoadingEffort(false);
        }
    }, [dealId]);

    // Fetch pipeline stages for the stage dropdown
    useEffect(() => {
        const load = async () => {
            try {
                const res  = await axiosInstance.get(`${base_url}/crm/pipeline/stages`);
                const raw  = res.data;
                const list = Array.isArray(raw?.data)
                    ? raw.data
                    : Array.isArray(raw?.data?.data)
                    ? raw.data.data
                    : [];
                setStages(sortStagesByOrdering(list));
            } catch (_) {}
        };
        load();
    }, []);

    const updateStage = async () => {
        if (!selectedStageId || !dealId) return;
        setUpdatingStage(true);
        try {
            await axiosInstance.patch(`${base_url}/crm/deals/${dealId}/stage`, {
                pipeline_stage_id: Number(selectedStageId),
            });
            notifyRef.current("Stage updated", "success");
            setStageEditing(false);
            fetchDeal();
            fetchTimeline();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to update stage", "error");
        } finally {
            setUpdatingStage(false);
        }
    };

    useEffect(() => {
        fetchDeal();
    }, [fetchDeal]);

    useEffect(() => {
        if (!isTaskDateFilterComplete(dateFrom, dateTo)) return;
        if (dateFrom && dateTo && !isValidTaskDateRange(dateFrom, dateTo)) return;
        fetchTasks(dateFrom, dateTo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealId, dateFrom, dateTo]);

    useEffect(() => {
        fetchTimeline();
        fetchNotes();
        fetchEffort();
    }, [fetchTimeline, fetchNotes, fetchEffort]);

    const handleTaskDateFromChange = (value) => setDateFrom(value);
    const handleTaskDateToChange = (value) => setDateTo(value);
    const handleClearTaskDateFilter = () => {
        setDateFrom("");
        setDateTo("");
    };
    const handleTaskDatePreset = (months) => {
        const range = getTaskDueDateRangeForMonths(months);
        setDateFrom(range.from);
        setDateTo(range.to);
    };
    const dateRangeInvalid = Boolean(
        (dateFrom && !dateTo) || (!dateFrom && dateTo) || (dateFrom && dateTo && !isValidTaskDateRange(dateFrom, dateTo))
    );
    const activePresetMonths = matchTaskDatePresetMonths(dateFrom, dateTo);
    const taskDateRangeNote = getTaskDateRangeStatusMessage(dateFrom, dateTo);
    const taskListEmptyMessage = getTaskDateFilterEmptyMessage(dateFrom, dateTo);

    const refreshDealAndTasks = (rangeOverride) => {
        const from = rangeOverride?.from ?? dateFrom;
        const to = rangeOverride?.to ?? dateTo;
        if (rangeOverride) {
            setDateFrom(from);
            setDateTo(to);
        }
        fetchDeal();
        fetchTasks(from, to);
    };

    const handleTaskCreated = ({ dueDate, isFollowUp: createdFollowUp } = {}) => {
        let rangeOverride;
        if (isTaskDateFilterActive(dateFrom, dateTo) && dueDate) {
            rangeOverride = expandTaskDateRangeToInclude(dateFrom, dateTo, dueDate);
        }
        if (createdFollowUp) setTab(0);
        refreshDealAndTasks(rangeOverride);
        fetchTimeline();
    };

    const saveNote = async () => {
        if (!noteInput.trim() || !contactId) return;
        setSavingNote(true);
        try {
            const body = { note: noteInput.trim() };
            if (dealId) body.deal_id = dealId;
            await axiosInstance.post(`${base_url}/crm/contacts/${contactId}/notes`, body);
            setNoteInput("");
            notifyRef.current("Note saved", "success");
            fetchNotes();
            fetchTimeline();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to save note", "error");
        } finally {
            setSavingNote(false);
        }
    };

    const deleteNote = async (noteId) => {
        try {
            await axiosInstance.delete(`${base_url}/crm/notes/${noteId}`);
            setNotes((prev) => prev.filter((n) => n.id !== noteId));
            notifyRef.current("Note deleted", "success");
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to delete note", "error");
        }
    };

    const completeTask = async (taskId) => {
        try {
            await axiosInstance.patch(`${base_url}/crm/tasks/${taskId}/complete`);
            setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: "completed" } : t));
            notifyRef.current("Task marked complete", "success");
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to update task", "error");
        }
    };

    const reopenTask = async (taskId) => {
        try {
            await axiosInstance.put(`${base_url}/crm/tasks/${taskId}`, { status: "pending" });
            setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: "pending" } : t));
            notifyRef.current("Task marked as pending", "info");
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to reopen task", "error");
        }
    };

    const toggleTask = (t) => {
        if (t.status === "completed") reopenTask(t.id);
        else completeTask(t.id);
    };

    const logCall = async () => {
        if (!dealId || loggingCall) return;
        setLoggingCall(true);
        try {
            const today = new Date().toISOString().split("T")[0];
            await axiosInstance.post(`${base_url}/crm/tasks`, {
                deal_id:  dealId,
                title:    `Call on ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`,
                type:     "call",
                due_date: today,
                status:   "completed",
            });
            notifyRef.current("Call logged successfully 📞", "success");
            refreshDealAndTasks();
            fetchTimeline();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to log call", "error");
        } finally {
            setLoggingCall(false);
        }
    };

    const logMessage = async () => {
        if (!dealId || loggingMessage) return false;
        setLoggingMessage(true);
        try {
            const today = new Date().toISOString().split("T")[0];
            await axiosInstance.post(`${base_url}/crm/tasks`, {
                deal_id:  dealId,
                title:    `Message on ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`,
                type:     "message",
                due_date: today,
                status:   "completed",
            });
            notifyRef.current("Message logged successfully 💬", "success");
            refreshDealAndTasks();
            fetchTimeline();
            return true;
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to log message", "error");
            return false;
        } finally {
            setLoggingMessage(false);
        }
    };


    const markWon = async () => {
        if (!dealId || actioning) return;
        setActioning(true);
        setWonConfirmOpen(false);
        try {
            await axiosInstance.post(`${base_url}/crm/deals/${dealId}/won`);
            notifyRef.current("Deal marked as Won 🎉", "success");
            handleBack();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed", "error");
        } finally {
            setActioning(false);
        }
    };

    const markLost = async () => {
        if (!dealId || actioning) return;
        setActioning(true);
        setLostConfirmOpen(false);
        try {
            await axiosInstance.post(`${base_url}/crm/deals/${dealId}/lost`);
            notifyRef.current("Deal marked as Lost", "info");
            handleBack();
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed", "error");
        } finally {
            setActioning(false);
        }
    };

    const isDealOpen = String(displayDeal?.status || "").toLowerCase() === "open";
    const contactName  = displayDeal?.contact?.name  || displayDeal?.title || "Deal";
    const companyName  = displayDeal?.contact?.company_name || "";
    const contactTags  = resolveContactTags(displayDeal?.contact);
    const stageName    = displayDeal?.stage?.stage_name    || displayDeal?.pipeline_stage?.stage_name || "";
    const effortByType = useMemo(
        () => normalizeDistributionPairs(effort?.distribution?.by_type, "type"),
        [effort]
    );
    const effortByStatus = useMemo(
        () => normalizeDistributionPairs(effort?.distribution?.by_status, "status"),
        [effort]
    );
    const hasTypeChartData = useMemo(
        () => effortByType.some((x) => Number(x.value) > 0),
        [effortByType]
    );
    const hasStatusChartData = useMemo(
        () => effortByStatus.some((x) => Number(x.value) > 0),
        [effortByStatus]
    );
    const effortTrendRecent = useMemo(
        () => (effort?.trend || []),
        [effort]
    );

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box sx={WB_SX.page}>
            {/* Header — title left, Mark Won / Lost top-right when deal is open */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0, flex: 1 }}>
                    <Button
                        startIcon={<ArrowBack fontSize="small" />}
                        onClick={handleBack}
                        size="small"
                        sx={{ ...WB_SX.secondaryButton, fontSize: "0.8rem" }}
                    >
                        Back
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                        {companyName ? `${companyName} — ` : ""}{contactName}
                    </Typography>
                    {stageName && <CRMBadge label={stageName} />}
                    {displayDeal?.status && displayDeal.status !== "open" && (
                        <CRMBadge
                            label={DEAL_STATUS_LABELS[displayDeal.status] ?? displayDeal.status}
                            bg={displayDeal.status === "won" ? "#d1fae5" : "#fee2e2"}
                            text={displayDeal.status === "won" ? "#065f46" : "#b91c1c"}
                        />
                    )}
                </Box>
                {isDealOpen && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0, ml: "auto" }}>
                        <Typography
                            variant="caption"
                            sx={{
                                px: 1.1,
                                py: 0.5,
                                borderRadius: 99,
                                backgroundColor: "rgba(17,24,39,0.06)",
                                color: "text.secondary",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Follow Ups: {totalFollowUps}
                        </Typography>
                        <Button
                            startIcon={<CheckCircle fontSize="small" />}
                            size="small"
                            variant="contained"
                            disabled={actioning}
                            onClick={() => setWonConfirmOpen(true)}
                            sx={{ ...WB_SX.primaryButton, fontSize: "0.75rem", whiteSpace: "nowrap" }}
                        >
                            Mark Won
                        </Button>
                        <Button
                            startIcon={<Cancel fontSize="small" />}
                            size="small"
                            disabled={actioning}
                            onClick={() => setLostConfirmOpen(true)}
                            sx={{
                                ...WB_SX.secondaryButton,
                                fontSize: "0.75rem",
                                whiteSpace: "nowrap",
                                color: "error.main",
                                borderColor: "error.main",
                                "&:hover": { backgroundColor: "rgba(239,68,68,0.06)", borderColor: "error.dark", color: "error.dark" },
                            }}
                        >
                            Lost
                        </Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", md: "nowrap" } }}>
                {/* Left panel */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
                    {/* Deal info */}
                    <Card elevation={0} sx={WB_SX.card}>
                        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                                <CRMAvatar initials={getInitials(contactName)} size={42} color="#1d4ed8" />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{contactName}</Typography>
                                    {companyName && (
                                        <Typography variant="caption" color="text.secondary">{companyName}</Typography>
                                    )}
                                </Box>
                                <Tooltip title={contactId ? "Open contact" : "No contact linked"}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={!contactId}
                                            onClick={() => window.open(`/crm/contacts/${contactId}`, "_blank", "noopener,noreferrer")}
                                            sx={{ color: "text.secondary", border: "1px solid #e5e7eb", "&:hover": { color: "#1d4ed8" } }}
                                        >
                                            <ContactPageOutlined sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            <Divider sx={{ mb: 1.75 }} />

                            {/* Deal title & description (API: PUT deal optional title, description) */}
                            <Box sx={{ mb: 1.75 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">Deal</Typography>
                                    {isDealOpen && (
                                        <Tooltip title="Edit title & description">
                                            <IconButton size="small" sx={{ p: 0.35 }} onClick={() => setDealEditOpen(true)}>
                                                <EditOutlined sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{displayDeal?.title || "—"}</Typography>
                                {displayDeal?.description ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                                        {displayDeal.description}
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>No description</Typography>
                                )}
                            </Box>

                            <Divider sx={{ mb: 1.75 }} />

                            {/* Static fields */}
                            {[
                                ["Deal Value",  fmtValue(displayDeal?.value)],
                                ["Assigned to", displayDeal?.current_assigned_user?.name || displayDeal?.assigned_user?.name || "—"],
                                ["Close Date",  fmtDate(displayDeal?.expected_close_date)],
                                ["Source",      displayDeal?.contact?.source || "—"],
                                ["Status",      DEAL_STATUS_LABELS[displayDeal?.status] ?? displayDeal?.status ?? "—"],
                            ].map(([k, v]) => (
                                <Box key={k} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.75 }}>
                                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{v}</Typography>
                                </Box>
                            ))}

                            {/* Stage — inline editable */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.75 }}>
                                <Typography variant="caption" color="text.secondary">Stage</Typography>
                                {stageEditing ? (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <FormControl size="small">
                                            <Select
                                                value={selectedStageId}
                                                onChange={(e) => setSelectedStageId(e.target.value)}
                                                disabled={updatingStage}
                                                sx={{ fontSize: "0.75rem", height: 28, borderRadius: 1.5, minWidth: 120 }}
                                            >
                                                {stages.map((s) => (
                                                    <MenuItem key={s.id ?? s.pipeline_stage_id} value={String(s.id ?? s.pipeline_stage_id)}>
                                                        {s.stage_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Tooltip title="Save">
                                            <IconButton
                                                size="small"
                                                onClick={updateStage}
                                                disabled={updatingStage || !selectedStageId}
                                                sx={{ color: "success.main", p: 0.4 }}
                                            >
                                                {updatingStage
                                                    ? <CircularProgress size={12} />
                                                    : <CheckOutlined sx={{ fontSize: 14 }} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Cancel">
                                            <IconButton size="small" onClick={() => setStageEditing(false)} sx={{ p: 0.4, color: "text.disabled" }}>
                                                <Close sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                            {stageName || "—"}
                                        </Typography>
                                        <Tooltip title="Change stage">
                                            <IconButton
                                                size="small"
                                                sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "primary.main" } }}
                                                onClick={() => {
                                                    const currentId = displayDeal?.pipeline_stage_id;
                                                    setSelectedStageId(currentId ? String(currentId) : "");
                                                    setStageEditing(true);
                                                }}
                                            >
                                                <EditOutlined sx={{ fontSize: 12 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Contact info */}
                    {displayDeal?.contact && (
                        <Card elevation={0} sx={WB_SX.card}>
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "text.secondary", display: "block", mb: 1.5 }}>
                                    Contact Info
                                </Typography>
                                {[
                                    {
                                        icon: <PhoneOutlined sx={{ fontSize: 14 }} />,
                                        label: "Phone",
                                        value: displayDeal.contact.phone,
                                        copy: true,
                                    },
                                    {
                                        icon: <PhoneOutlined sx={{ fontSize: 14 }} />,
                                        label: "Alternate Phone",
                                        value: displayDeal.contact.alternate_contact_number,
                                        copy: true,
                                    },
                                    {
                                        icon: <EmailOutlined sx={{ fontSize: 14 }} />,
                                        label: "Email",
                                        value: displayDeal.contact.email,
                                        copy: true,
                                    },
                                    {
                                        icon: <BusinessOutlined sx={{ fontSize: 14 }} />,
                                        label: "Company",
                                        value: displayDeal.contact.company_name,
                                    },
                                    {
                                        icon: <LocationOnOutlined sx={{ fontSize: 14 }} />,
                                        label: "Location",
                                        value: displayDeal.contact.location,
                                    },
                                    {
                                        icon: <PersonOutlined sx={{ fontSize: 14 }} />,
                                        label: "Source",
                                        value: displayDeal.contact.source,
                                    },
                                ].map(({ icon, label, value, copy }) =>
                                    value ? (
                                        <Box
                                            key={label}
                                            sx={{
                                                display: "flex", alignItems: "center", gap: 1,
                                                py: 0.75,
                                                borderBottom: `1px solid rgba(0,0,0,0.05)`,
                                                "&:last-child": { borderBottom: "none" },
                                            }}
                                        >
                                            <Box sx={{ color: "text.disabled", display: "flex", flexShrink: 0 }}>{icon}</Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="caption" color="text.disabled" sx={{ display: "block", lineHeight: 1.2 }}>
                                                    {label}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ fontWeight: 600, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                    title={value}
                                                >
                                                    {value}
                                                </Typography>
                                            </Box>
                                            {copy && (
                                                <Tooltip title="Copy">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.3, color: "text.disabled", flexShrink: 0, "&:hover": { color: "primary.main" } }}
                                                        onClick={() => { navigator.clipboard.writeText(value); notifyRef.current(`${label} copied`, "success"); }}
                                                    >
                                                        <ContentCopyOutlined sx={{ fontSize: 12 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    ) : null
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        py: 0.75,
                                    }}
                                >
                                    <Box sx={{ color: "text.disabled", display: "flex", flexShrink: 0, mt: 0.15 }}>
                                        <LocalOfferOutlined sx={{ fontSize: 14 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" color="text.disabled" sx={{ display: "block", lineHeight: 1.2, mb: 0.5 }}>
                                            Tags
                                        </Typography>
                                        {contactTags.length > 0 ? (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    px: 1,
                                                    py: 0.6,
                                                    borderRadius: 1.5,
                                                    border: "1px solid #e5e7eb",
                                                    backgroundColor: "#f9fafb",
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mr: 0.25 }}>
                                                    Tags:
                                                </Typography>
                                                {contactTags.map((t, ti) => {
                                                    const tc = TAG_COLOR_PALETTE[ti % TAG_COLOR_PALETTE.length];
                                                    return (
                                                        <CRMBadge key={t.id ?? ti} label={t.tag_name} bg={tc.bg} text={tc.text} />
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">—</Typography>
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Quick actions */}
                    <Card elevation={0} sx={WB_SX.card}>
                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "text.secondary", display: "block", mb: 1.25 }}>
                                Quick Actions
                            </Typography>
                            {QUICK_ACTIONS.map(({ icon, label }) => {
                                const phone = displayDeal?.contact?.phone;
                                const isLoggingQuickAction =
                                    (label === "Log Call" && loggingCall) ||
                                    (label === "Log Message" && loggingMessage);
                                const handleClick = async () => {
                                    if (label === "Add Task") setAddTaskOpen(true);
                                    if (label === "Create Follow-up") setAddFollowUpOpen(true);
                                    if (label === "Log Call") logCall();
                                    if (label === "Log Message") logMessage();
                                    if (label === "Send WhatsApp") {
                                        if (!phone) { notifyRef.current("No phone number for this contact", "warning"); return; }
                                        await logMessage();
                                        window.open(`/whatsapp-business/chat-window/contact/${phone}`, "_blank", "noopener,noreferrer");
                                    }
                                };
                                return (
                                    <Box
                                        key={label}
                                        onClick={isLoggingQuickAction ? undefined : handleClick}
                                        sx={{
                                            display: "flex", alignItems: "center", gap: 1.25,
                                            py: 0.875, borderBottom: `1px solid ${WB_UI.color.border}`,
                                            cursor: isLoggingQuickAction ? "default" : "pointer",
                                            opacity: isLoggingQuickAction ? 0.6 : 1,
                                            "&:last-child": { borderBottom: "none" },
                                            "&:hover": { color: isLoggingQuickAction ? "inherit" : "primary.main" },
                                            transition: "color 0.15s, opacity 0.15s",
                                        }}
                                    >
                                        <Box sx={{ color: "text.secondary", display: "flex" }}>
                                            {isLoggingQuickAction ? <CircularProgress size={14} sx={{ color: "text.secondary" }} /> : icon}
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {isLoggingQuickAction ? "Logging…" : label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </CardContent>
                    </Card>
                </Box>

                {/* Right panel */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            mb: 2,
                            borderBottom: `1px solid ${WB_UI.color.border}`,
                            "& .MuiTab-root": { textTransform: "capitalize", fontWeight: 600, fontSize: "0.82rem" },
                            "& .Mui-selected": { color: "text.primary !important", fontWeight: 700 },
                            "& .MuiTabs-indicator": { backgroundColor: "#111827" },
                        }}
                    >
                        <Tab label={`Tasks (${tasks.length})`} />
                        <Tab label={`Notes (${notes.length})`} />
                        <Tab label={`Products / services (${dealLineItems.length})`} />
                        <Tab label={`Activities (${timeline.length})`} />
                        <Tab label="Effort" />
                    </Tabs>

                    {/* Tasks tab */}
                    {tab === 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                                    <TextField
                                        label="From"
                                        type="date"
                                        size="small"
                                        value={dateFrom}
                                        onChange={(e) => handleTaskDateFromChange(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        error={dateRangeInvalid}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, minWidth: 150 }}
                                    />
                                    <TextField
                                        label="To"
                                        type="date"
                                        size="small"
                                        value={dateTo}
                                        onChange={(e) => handleTaskDateToChange(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        error={dateRangeInvalid}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, minWidth: 150 }}
                                    />
                                    <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                                        {TASK_DATE_PRESETS.map((preset) => {
                                            const isActive = activePresetMonths === preset.months;
                                            return (
                                                <Chip
                                                    key={preset.label}
                                                    label={preset.label}
                                                    size="small"
                                                    variant={isActive ? "filled" : "outlined"}
                                                    onClick={() => handleTaskDatePreset(preset.months)}
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: "0.75rem",
                                                        borderRadius: 999,
                                                        minWidth: 40,
                                                        cursor: "pointer",
                                                        ...(isActive && {
                                                            backgroundColor: "#111827",
                                                            color: "#fff",
                                                            "&:hover": { backgroundColor: "#1f2937" },
                                                        }),
                                                    }}
                                                />
                                            );
                                        })}
                                        <Chip
                                            label="Clear"
                                            size="small"
                                            variant={!dateFrom && !dateTo ? "filled" : "outlined"}
                                            onClick={handleClearTaskDateFilter}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "0.75rem",
                                                borderRadius: 999,
                                                cursor: "pointer",
                                                ...(!dateFrom && !dateTo && {
                                                    backgroundColor: "#111827",
                                                    color: "#fff",
                                                    "&:hover": { backgroundColor: "#1f2937" },
                                                }),
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddTask fontSize="small" />}
                                    onClick={() => setAddTaskOpen(true)}
                                    sx={WB_SX.primaryButton}
                                >
                                    Add Task
                                </Button>
                            </Box>
                            <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.8rem" } }}>
                                {taskDateRangeNote}
                            </Alert>
                            {loadingTasks ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : tasks.length === 0 ? (
                                <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: "center" }}>
                                    {taskListEmptyMessage}
                                </Typography>
                            ) : (
                                tasks.map((t) => (
                                    <Card key={t.id} elevation={0} sx={WB_SX.card}>
                                        <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 }, display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Checkbox
                                                size="small"
                                                checked={t.status === "completed"}
                                                onChange={() => toggleTask(t)}
                                                sx={{
                                                    p: 0.5,
                                                    color: "#d1d5db",
                                                    "&.Mui-checked": { color: "#111827" },
                                                    "& .MuiSvgIcon-root": { fontSize: 20, borderRadius: 4 },
                                                }}
                                            />
                                            <Box sx={{ fontSize: 16 }}>{taskIcon(t.type)}</Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: t.status === "completed" ? "line-through" : "none", color: t.status === "completed" ? "text.disabled" : "text.primary" }}>
                                                    {getTaskDisplayTitle(t)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {getTaskAssigneeName(t) ? `Assigned to ${getTaskAssigneeName(t)}` : ""}
                                                    {t.due_date ? ` · Due ${fmtDate(t.due_date)}` : ""}
                                                </Typography>
                                            </Box>
                                            <CRMBadge
                                                label={TASK_STATUS_LABELS[t.status] ?? t.status}
                                                bg={t.status === "overdue" ? "#fee2e2" : t.status === "completed" ? "#d1fae5" : "#f3f4f6"}
                                                text={t.status === "overdue" ? "#b91c1c" : t.status === "completed" ? "#065f46" : "#374151"}
                                            />
                                            <Tooltip title="Edit task">
                                                <IconButton size="small" onClick={() => setEditTask(t)} sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}>
                                                    <EditOutlined sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </Box>
                    )}

                    {/* Notes tab */}
                    {tab === 1 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={noteInput}
                                    onChange={(e) => setNoteInput(e.target.value)}
                                    placeholder="Add a note..."
                                    size="small"
                                    disabled={savingNote}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={saveNote}
                                    disabled={savingNote || !noteInput.trim()}
                                    sx={{ ...WB_SX.primaryButton, alignSelf: "flex-end", whiteSpace: "nowrap" }}
                                >
                                    {savingNote ? "Saving..." : "Save"}
                                </Button>
                            </Box>
                            {notes.length === 0 ? (
                                <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: "center" }}>No notes yet.</Typography>
                            ) : (
                                notes.map((n) => (
                                    <Card key={n.id} elevation={0} sx={WB_SX.card}>
                                        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, display: "flex", gap: 1.5 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ lineHeight: 1.65 }}>{n.note || n.text}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                                    {n.user?.name || n.created_by || "You"} · {n.created_at ? new Date(n.created_at).toLocaleString("en-IN", { month: "short", day: "numeric" }) : ""}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "column", alignSelf: "flex-start" }}>
                                                <Tooltip title="Edit note">
                                                    <IconButton size="small" onClick={() => setEditNote(n)} sx={{ color: "text.disabled", "&:hover": { color: "primary.main" } }}>
                                                        <EditOutlined sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete note">
                                                    <IconButton size="small" onClick={() => deleteNote(n.id)} sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}>
                                                        ×
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </Box>
                    )}

                    {/* Products / services tab */}
                    {tab === 2 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                Search and choose products or services for this deal. Saving updates what is linked here;
                                clearing all selections and saving removes every link on the deal.
                            </Typography>
                            {dealLineItems.length === 0 && !loadingCatalog ? (
                                <Typography variant="body2" color="text.disabled" sx={{ py: 1, textAlign: "center" }}>
                                    No products or services linked yet.
                                </Typography>
                            ) : null}
                            <Autocomplete
                                multiple
                                loading={loadingCatalog}
                                options={catalogProducts}
                                getOptionLabel={catalogOptionLabel}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={lineItemsForAutocomplete}
                                onChange={(_, newValue) => setDealLineItems(newValue)}
                                filterSelectedOptions
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.id}
                                            size="small"
                                            variant="outlined"
                                            label={option.name}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Select products / services"
                                        placeholder={loadingCatalog ? "Loading catalog…" : "Search catalog…"}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingCatalog ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                                    />
                                )}
                            />
                            {lineItemsForAutocomplete.length > 0 ? (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
                                        On this deal — view full details
                                    </Typography>
                                    <Grid container spacing={1.25}>
                                        {lineItemsForAutocomplete.map((item) => (
                                            <Grid item xs={12} sm={6} key={item.id}>
                                                <Card elevation={0} sx={{ ...WB_SX.card, height: "100%" }}>
                                                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }} noWrap title={item.name}>
                                                                    {item.name}
                                                                </Typography>
                                                                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.75, alignItems: "center" }}>
                                                                    <Chip
                                                                        size="small"
                                                                        label={item.type === "service" ? "Service" : "Product"}
                                                                        sx={{
                                                                            height: 22,
                                                                            fontSize: "0.7rem",
                                                                            fontWeight: 600,
                                                                            backgroundColor: item.type === "service" ? "#eff6ff" : "#f0fdf4",
                                                                            color: item.type === "service" ? "#1d4ed8" : "#166534",
                                                                        }}
                                                                    />
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {fmtValue(item.price)}
                                                                    </Typography>
                                                                    {item.sku ? (
                                                                        <Typography variant="caption" color="text.disabled" noWrap title={item.sku}>
                                                                            SKU {item.sku}
                                                                        </Typography>
                                                                    ) : null}
                                                                </Box>
                                                            </Box>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<VisibilityOutlined sx={{ fontSize: 16 }} />}
                                                                onClick={() => openProductDetail(item)}
                                                                sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600, fontSize: "0.72rem" }}
                                                            >
                                                                Details
                                                            </Button>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            ) : null}
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={saveDealProducts}
                                    disabled={savingDealProducts || loadingCatalog}
                                    sx={WB_SX.primaryButton}
                                >
                                    {savingDealProducts ? "Saving…" : "Save"}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Activities tab */}
                    {tab === 3 && (
                        <Box>
                            {timeline.length === 0 ? (
                                <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: "center" }}>
                                    No activity yet.
                                </Typography>
                            ) : (
                                timeline.map((item, i) => (
                                    <Box key={item.id || i} sx={{ display: "flex", gap: 1.75, py: 1.75, borderBottom: `1px solid ${WB_UI.color.border}` }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                            <Box sx={{ width: 32, height: 32, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
                                                {activityIcon(item.type, item.source)}
                                            </Box>
                                            <Typography variant="caption" sx={{ fontSize: "0.6rem", color: "text.disabled", textAlign: "center", lineHeight: 1.2, mt: 0.25, maxWidth: 36, wordBreak: "break-word" }}>
                                                {(item.type || "").replace(/_/g, " ")}
                                            </Typography>
                                            {i < timeline.length - 1 && <Box sx={{ width: 1, flex: 1, background: "#e5e7eb", mt: 0.5 }} />}
                                        </Box>
                                        <Box sx={{ flex: 1, pb: 0.5 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                                                {(() => {
                                                    const raw = item.description || item.note || item.text || item.type || "";
                                                    const match = raw.match(/^(.*stage:\s*)(\d+)(.*)$/i);
                                                    if (match) {
                                                        const s = stages.find((st) => String(st.id ?? st.pipeline_stage_id) === String(match[2]));
                                                        const stageName = s?.stage_name || match[2];
                                                        return (
                                                            <>
                                                                {match[1]}
                                                                <Box component="span" sx={{ fontWeight: 700, color: "#1d4ed8", backgroundColor: "#eff6ff", px: 0.6, py: 0.15, borderRadius: 1, fontSize: "0.8rem" }}>
                                                                    {stageName}
                                                                </Box>
                                                                {match[3]}
                                                            </>
                                                        );
                                                    }
                                                    return raw;
                                                })()}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                                {item.created_by_name || item.user?.name || item.performed_by || "—"} · {item.created_at ? new Date(item.created_at).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </Box>
                    )}

                    {/* Effort tab */}
                    {tab === 4 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                            {loadingEffort ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={26} />
                                </Box>
                            ) : effortError ? (
                                <Alert severity="error">{effortError}</Alert>
                            ) : !effort ? (
                                <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: "center" }}>
                                    No effort analytics available for this deal.
                                </Typography>
                            ) : (
                                <>
                                    <Grid container spacing={1.25}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Card elevation={0} sx={WB_SX.card}>
                                                <CardContent sx={{ p: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary">Efforts (window)</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                                                        {effort?.summary?.efforts_in_window ?? 0}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Card elevation={0} sx={WB_SX.card}>
                                                <CardContent sx={{ p: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary">Efforts (all time)</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                                                        {effort?.summary?.efforts_all_time ?? 0}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Card elevation={0} sx={WB_SX.card}>
                                                <CardContent sx={{ p: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary">Completion rate</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25, color: "success.main" }}>
                                                        {fmtPercent(effort?.summary?.completion_rate_in_window)}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Card elevation={0} sx={WB_SX.card}>
                                                <CardContent sx={{ p: 1.5 }}>
                                                    <Typography variant="caption" color="text.secondary">Overdue rate</Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25, color: "error.main" }}>
                                                        {fmtPercent(effort?.summary?.overdue_rate_in_window)}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={1.25}>
                                        <Grid item xs={12} md={6}>
                                            <Card elevation={0} sx={WB_SX.card}>
                                                <CardContent sx={{ p: 1.75 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.25 }}>By Type</Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
                                                        {effortByType.length === 0 ? (
                                                            <Typography variant="caption" color="text.disabled">No type distribution yet.</Typography>
                                                        ) : (
                                                            effortByType.map(({ key, value }) => (
                                                                <Chip key={key} size="small" label={`${startCase(key)}: ${value}`} />
                                                            ))
                                                        )}
                                                    </Box>
                                                    {hasTypeChartData ? (
                                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                            <PieChart
                                                                height={210}
                                                                series={[
                                                                    {
                                                                        data: effortByType.map((x, i) => ({
                                                                            id: i,
                                                                            label: startCase(x.key),
                                                                            value: x.value,
                                                                        })),
                                                                        innerRadius: 42,
                                                                        outerRadius: 80,
                                                                        paddingAngle: 2,
                                                                    },
                                                                ]}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">
                                                            No data available
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Card elevation={0} sx={WB_SX.card}>
                                                <CardContent sx={{ p: 1.75 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.25 }}>By Status</Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
                                                        {effortByStatus.length === 0 ? (
                                                            <Typography variant="caption" color="text.disabled">No status distribution yet.</Typography>
                                                        ) : (
                                                            effortByStatus.map(({ key, value }) => (
                                                                <Chip
                                                                    key={key}
                                                                    size="small"
                                                                    label={`${startCase(key)}: ${value}`}
                                                                    sx={
                                                                        key === "completed"
                                                                            ? { backgroundColor: "#dcfce7", color: "#166534" }
                                                                            : key === "overdue"
                                                                                ? { backgroundColor: "#fee2e2", color: "#b91c1c" }
                                                                                : {}
                                                                    }
                                                                />
                                                            ))
                                                        )}
                                                    </Box>
                                                    {hasStatusChartData ? (
                                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                            <PieChart
                                                                height={210}
                                                                series={[
                                                                    {
                                                                        data: effortByStatus.map((x, i) => ({
                                                                            id: i,
                                                                            label: startCase(x.key),
                                                                            value: x.value,
                                                                        })),
                                                                        innerRadius: 42,
                                                                        outerRadius: 80,
                                                                        paddingAngle: 2,
                                                                    },
                                                                ]}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" color="text.disabled">
                                                            No data available
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    <Card elevation={0} sx={WB_SX.card}>
                                        <CardContent sx={{ p: 1.75 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Effort Trend (Recent)</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
                                                Window: {fmtDate(effort?.window?.from)} to {fmtDate(effort?.window?.to)} ({Math.round(Number(effort?.window?.days || 0))} days)
                                            </Typography>
                                            {effortTrendRecent.length === 0 ? (
                                                <Typography variant="caption" color="text.disabled">No trend data in this window.</Typography>
                                            ) : (
                                                <>
                                                    <Box sx={{ width: "100%", overflowX: "auto", mb: 1 }}>
                                                        <LineChart
                                                            height={220}
                                                            xAxis={[
                                                                {
                                                                    scaleType: "band",
                                                                    data: effortTrendRecent.map((row) => fmtDate(row.date)),
                                                                },
                                                            ]}
                                                            series={[
                                                                {
                                                                    label: "Total Efforts",
                                                                    data: effortTrendRecent.map((row) => Number(row.total) || 0),
                                                                    color: "#4f46e5",
                                                                },
                                                            ]}
                                                            margin={{ left: 36, right: 14, top: 16, bottom: 36 }}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                        </CardContent>
                                    </Card>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Add Task Dialog */}
            <AddTaskDialog
                open={addTaskOpen}
                dealId={dealId}
                onClose={() => setAddTaskOpen(false)}
                onCreated={handleTaskCreated}
            />
            <AddTaskDialog
                open={addFollowUpOpen}
                dealId={dealId}
                mode="follow-up"
                onClose={() => setAddFollowUpOpen(false)}
                onCreated={handleTaskCreated}
            />
            <EditTaskDialog
                open={!!editTask}
                task={editTask}
                onClose={() => setEditTask(null)}
                onUpdated={() => { refreshDealAndTasks(); fetchTimeline(); }}
            />
            <EditNoteDialog
                open={!!editNote}
                note={editNote}
                onClose={() => setEditNote(null)}
                onUpdated={() => { fetchNotes(); fetchTimeline(); }}
            />
            <EditDealDetailsDialog
                open={dealEditOpen}
                dealId={dealId}
                initialTitle={deal?.title ?? dealProp?.title}
                initialDescription={deal?.description ?? dealProp?.description}
                onClose={() => setDealEditOpen(false)}
                onSaved={() => { fetchDeal(); fetchTimeline(); }}
            />
            <CRMProductServiceDetailDialog
                open={productDetailOpen}
                productId={productDetailId}
                initialProduct={productDetailInitial}
                onClose={() => {
                    setProductDetailOpen(false);
                    setProductDetailId(null);
                    setProductDetailInitial(null);
                }}
            />

            {/* Mark Won confirmation */}
            <Dialog open={wonConfirmOpen} onClose={() => !actioning && setWonConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(22,163,74,0.08)", display: "grid", placeItems: "center" }}>
                            <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>Mark as Won?</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setWonConfirmOpen(false)} disabled={actioning}><Close fontSize="small" /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Are you sure you want to mark the deal for{" "}
                        <strong>{contactName}</strong> as <strong style={{ color: "#16a34a" }}>Won</strong>?
                        This will close the deal and move it out of the active pipeline.
                    </Typography>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button size="small" onClick={() => setWonConfirmOpen(false)} disabled={actioning} sx={WB_SX.secondaryButton}>Cancel</Button>
                    <Button
                        size="small" variant="contained"
                        onClick={markWon}
                        disabled={actioning}
                        sx={{ ...WB_SX.primaryButton, backgroundColor: "success.main", "&:hover": { backgroundColor: "success.dark" } }}
                    >
                        {actioning ? "Saving…" : "Yes, Mark Won 🎉"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mark Lost confirmation */}
            <Dialog open={lostConfirmOpen} onClose={() => !actioning && setLostConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "50%", backgroundColor: "rgba(239,68,68,0.08)", display: "grid", placeItems: "center" }}>
                            <Cancel sx={{ fontSize: 16, color: "error.main" }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>Mark as Lost?</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setLostConfirmOpen(false)} disabled={actioning}><Close fontSize="small" /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Are you sure you want to mark the deal for{" "}
                        <strong>{contactName}</strong> as <strong style={{ color: "#dc2626" }}>Lost</strong>?
                        This will close the deal as unsuccessful.
                    </Typography>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button size="small" onClick={() => setLostConfirmOpen(false)} disabled={actioning} sx={WB_SX.secondaryButton}>Cancel</Button>
                    <Button
                        size="small" variant="contained"
                        onClick={markLost}
                        disabled={actioning}
                        sx={{ ...WB_SX.primaryButton, backgroundColor: "error.main", "&:hover": { backgroundColor: "error.dark" } }}
                    >
                        {actioning ? "Saving…" : "Yes, Mark Lost"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CRMDealDetail;
